import {
  createStatsigEvent,
  useRoutePathname,
  useStatsigClient,
  type StatsigEventName,
} from "@repo/statsig";
import Image from "next/image";
import menuButtonChevron from "public/icons/MenuButtonChevron.svg";
import React from "react";
import { BodyText } from "src/components/KinsaTypography";

interface MenuButtonProps {
  icon: React.ReactNode;
  text: string;
  onClick?: () => void;
  disabled?: boolean;
  href?: string;
  statsigEvent?: StatsigEventName;
}

export const MenuButton: React.FC<MenuButtonProps> = ({
  icon,
  text,
  onClick,
  disabled = false,
  href,
  statsigEvent,
}) => {
  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();

  const handleClick = () => {
    // Log custom event if provided
    if (statsigEvent) {
      try {
        const event = createStatsigEvent(statsigEvent, locationData);
        logEvent(event);
      } catch (error) {
        // Don't block user interaction if Statsig logging fails
        console.warn("[Statsig] Failed to log event:", error);
      }
    }

    onClick?.();
  };
  const buttonContent = (
    <>
      <div className="flex items-center gap-3">
        {icon}
        <BodyText className="text-left text-[17px]">{text}</BodyText>
      </div>
      <Image
        src={menuButtonChevron}
        alt="Go"
        width={7.5}
        height={12}
        unoptimized
      />
    </>
  );

  const buttonClassName = `flex items-center justify-between w-full py-6 px-0 transition-colors text-black-200 ${
    disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-surface"
  }`;

  if (href && !disabled) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={buttonClassName}
        onClick={handleClick}
      >
        {buttonContent}
      </a>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={disabled}
      className={buttonClassName}
    >
      {buttonContent}
    </button>
  );
};
